import { Dispatch } from "redux";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { updateIdentityProviderChangeRequestRequest } from "@requests/identity-provider";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { resources } from "@constants/hardcoded-strings";
import { ModalContextData } from "@idw-types/context";
import { ModalTypes } from "@enums/modal-types";
import { IdentityProviderChangeRequestResponse } from "@idw-types/data/identity-providers";
import { CreateIdentityProviderChangeRequestModal } from "@containers/modals/create-identity-provider-change-request-modal/create-identity-provider-change-request-modal";
import { SubmitIdentityProviderChangeRequestModal } from "@containers/modals/submit-identity-provider-change-request-modal/submit-identity-provider-change-request-modal";
import { CancelIdentityProviderChangeRequestModal } from "@containers/modals/cancel-identity-provider-change-request-modal/cancel-identity-provider-change-request-modal";
import { UnsubmitIdentityProviderChangeRequestModal } from "@containers/modals/unsubmit-identity-provider-change-request-modal/unsubmit-identity-provider-change-request-modal";
import { DeployNowIdentityProviderChangeRequestModal } from "@containers/modals/deploy-now-identity-provider-change-request-modal/deploy-now-identity-provider-change-request-modal";
import { RejectIdentityProviderChangeRequestModal } from "@containers/modals/reject-identity-provider-change-request-modal/reject-identity-provider-change-request-modal";
import { ApproveIdentityProviderChangeRequestModal } from "@containers/modals/approve-identity-provider-change-request-modal/approve-identity-provider-change-request-modal";

export const performIdentityProviderChangeRequestStep = async (
    dispatch: Dispatch,
    identityProviderId: string,
    changeRequestStatus: ChangeRequestStatusTypes,
    body: { [key: string]: any }
) => {
    const { data, error } = await updateIdentityProviderChangeRequestRequest(identityProviderId, body, changeRequestStatus);

    if (data?.transferObject) {
        return data.transferObject;
    } else if (error) {
        logAndDisplayErrorModal(
            dispatch,
            error,
            resources.requests.updateIdentityProviderChangeRequest.error.title,
            resources.requests.updateIdentityProviderChangeRequest.error.message,
            error,
            null,
            body
        );
    }
    return null;
};

export const deployIdentityProvider = async (
    dispatch: Dispatch,
    identityProviderId: string,
    changeRequestStatuses: Array<ChangeRequestStatusTypes>,
    currentStepIndex: number
): Promise<any> => {
    const currentChangeRequestStatus = changeRequestStatuses[currentStepIndex];

    if (currentStepIndex < changeRequestStatuses.length) {
        const body = changeRequestStatuses[currentStepIndex] === ChangeRequestStatusTypes.DEPLOYED
            ? { deploymentDate: new Date().getTime() }
            : null;

        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderId,
            currentChangeRequestStatus,
            body
        );

        if (response && changeRequestStatuses[currentStepIndex] !== ChangeRequestStatusTypes.DEPLOYED) {
            return await deployIdentityProvider(dispatch, identityProviderId, changeRequestStatuses, currentStepIndex + 1);
        } else {
            return response;
        }
    }

    return null;
};


export const clickIdentityProviderChangeRequestActionHandler = (
    dispatch: Dispatch,
    onSubmitHandler: (onModalClose: () => void, data?: any) => Promise<IdentityProviderChangeRequestResponse>,
    modalContext: ModalContextData,
    identityProviderId: string,
    identityProviderName: string,
    modalType: ModalTypes
) => {
    if (identityProviderId) {
        const renderModal = (onModalClose: () => void) => {
            switch (modalType) {
                case ModalTypes.CreateIdentityProviderChangeRequest:
                    return <CreateIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        identityProviderName={identityProviderName}
                    />;
                case ModalTypes.SubmitIdentityProviderChangeRequest:
                    return <SubmitIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        identityProviderName={identityProviderName}
                    />;
                case ModalTypes.UnsubmitIdentityProviderChangeRequest:
                    return <UnsubmitIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        identityProviderName={identityProviderName}
                    />;
                case ModalTypes.ApproveIdentityProviderChangeRequest:
                    return <ApproveIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                    />;
                case ModalTypes.RejectIdentityProviderChangeRequest:
                    return <RejectIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                    />
                case ModalTypes.CancelIdentityProviderChangeRequest:
                    return <CancelIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        identityProviderName={identityProviderName}
                    />;
                case ModalTypes.DeployNowIdentityProviderChangeRequest:
                    return <DeployNowIdentityProviderChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        identityProviderName={identityProviderName}
                    />;
                default:
                    return null;
            }
        }

        modalContext.setModalState({
            modalType: modalType,
            renderFunction: renderModal
        });
    } else {
        logAndDisplayErrorModal(
            dispatch,
            null,
            resources.pages.identityProviders.errors.invalidConfiguration.title,
            resources.pages.identityProviders.errors.invalidConfiguration.message,
        );
    }
};